import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AuthCallback from "@/pages/AuthCallback";
import Approvals from "@/pages/Approvals";
import { RealtimeProvider } from "@/lib/realtime";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { TenantProvider } from "@/lib/tenant";
import Login from "@/pages/Login";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/AuthExtras";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Endpoints from "@/pages/Endpoints";
import Incidents from "@/pages/Incidents";
import EmailSecurity from "@/pages/EmailSecurity";
import Identity from "@/pages/Identity";
import Vulnerabilities from "@/pages/Vulnerabilities";
import Network from "@/pages/Network";
import Compliance from "@/pages/Compliance";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import ReceiptPage from "@/pages/Receipt";
import PublicLayout from "@/site/PublicLayout";
import Home from "@/site/Home";
import Contact from "@/site/Contact";
import { Product, Pricing, Solutions, Resources, About, Legal } from "@/site/Pages";
import StatusPage from "@/site/Status";

export const homeFor = (user) => (user?.role === "customer" ? "/customer" : "/admin");

function Protected({ area, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading CyboScan…</div>;
  if (!user) return <Navigate to={`/${area}/login`} replace />;
  if (homeFor(user) !== `/${area}`) return <Navigate to={homeFor(user)} replace />;
  return children;
}

const appRoutes = (
  <>
    <Route index element={<Dashboard />} />
    <Route path="approvals" element={<Approvals />} />
    <Route path="endpoints" element={<Endpoints />} />
    <Route path="incidents" element={<Incidents />} />
    <Route path="email" element={<EmailSecurity />} />
    <Route path="identity" element={<Identity />} />
    <Route path="vulnerabilities" element={<Vulnerabilities />} />
    <Route path="network" element={<Network />} />
    <Route path="compliance" element={<Compliance />} />
    <Route path="reports" element={<Reports />} />
    <Route path="billing" element={<Billing />} />
    <Route path="billing/receipt/:txnId" element={<ReceiptPage />} />
    <Route path="settings" element={<Settings />} />
  </>
);

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/product" element={<Product />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal/:slug" element={<Legal />} />
      </Route>
      <Route path="/login" element={<Navigate to="/customer/login" replace />} />
      <Route path="/register" element={<Navigate to="/customer/register" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/customer/login" element={<Login area="customer" />} />
      <Route path="/customer/register" element={<Login area="customer" register />} />
      <Route path="/admin/login" element={<Login area="admin" />} />
      <Route path="/admin" element={<Protected area="admin"><RealtimeProvider><Layout base="/admin" /></RealtimeProvider></Protected>}>{appRoutes}</Route>
      <Route path="/customer" element={<Protected area="customer"><RealtimeProvider><Layout base="/customer" /></RealtimeProvider></Protected>}>{appRoutes}</Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
            <Toaster theme="dark" position="top-right" toastOptions={{
              style: { background: "#1B1B28", border: "1px solid rgba(255,255,255,0.08)", color: "#fafafa", borderRadius: "2px" }
            }} />
            <AppRoutes />
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
